import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Plus, Ship } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TESTIDS } from "@/constants/testIds";

export default function PortCalls() {
  const [list, setList] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [berths, setBerths] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    vessel_id: "", terminal_id: "", berth_id: "",
    eta: new Date(Date.now() + 6 * 3600 * 1000).toISOString().slice(0, 16),
    etd: new Date(Date.now() + 18 * 3600 * 1000).toISOString().slice(0, 16),
    side_to_quay: "starboard", bow_position_m: 30, purpose: "passenger",
    passenger_count: 0, cargo_summary: "", notes: "", status: "draft",
    requires_manoeuvring_plan: false, requires_spatial_bathymetry: false,
    requires_mooring_plan: false,
  });

  const refresh = async () => {
    const [pcs, vs, ts, bs] = await Promise.all([
      api.list("port-calls"), api.list("vessels"), api.list("terminals"), api.list("berths"),
    ]);
    setList(pcs); setVessels(vs); setTerminals(ts); setBerths(bs);
  };
  useEffect(() => { refresh(); }, []);

  const create = async () => {
    if (!form.vessel_id || !form.terminal_id) return toast.error("Vessel and terminal required");
    await api.create("port-calls", {
      ...form,
      eta: new Date(form.eta).toISOString(),
      etd: new Date(form.etd).toISOString(),
    });
    setOpen(false);
    toast.success("Port call created");
    refresh();
  };

  const vName = id => vessels.find(v => v.id === id)?.name || id;
  const tName = id => terminals.find(t => t.id === id)?.name || id;
  const bName = id => berths.find(b => b.id === id)?.name || "—";

  const filteredBerths = berths.filter(b => b.terminal_id === form.terminal_id);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-slate-500">Schedule</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-slate-50">Port Calls</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={TESTIDS.btnCreatePortCall} className="bg-sky-600 hover:bg-sky-500 rounded-none"><Plus size={14} className="mr-1.5" /> New Port Call</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0E1522] border border-[#1E293B] rounded-none text-slate-100 max-w-xl">
            <DialogHeader><DialogTitle className="font-heading">New port call</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Vessel">
                <Select value={form.vessel_id} onValueChange={v => setForm({ ...form, vessel_id: v })}>
                  <SelectTrigger data-testid="input-pc-vessel" className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue placeholder="Select vessel..." /></SelectTrigger>
                  <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none">{vessels.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Terminal">
                <Select value={form.terminal_id} onValueChange={v => setForm({ ...form, terminal_id: v, berth_id: "" })}>
                  <SelectTrigger data-testid="input-pc-terminal" className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue placeholder="Select terminal..." /></SelectTrigger>
                  <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none">{terminals.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Berth">
                <Select value={form.berth_id} onValueChange={v => setForm({ ...form, berth_id: v })}>
                  <SelectTrigger data-testid="input-pc-berth" className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue placeholder="Optional..." /></SelectTrigger>
                  <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none">
                    {filteredBerths.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Side to quay">
                <Select value={form.side_to_quay} onValueChange={v => setForm({ ...form, side_to_quay: v })}>
                  <SelectTrigger className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none"><SelectItem value="port">port</SelectItem><SelectItem value="starboard">starboard</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="ETA (local)"><Input type="datetime-local" value={form.eta} onChange={e => setForm({ ...form, eta: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
              <Field label="ETD (local)"><Input type="datetime-local" value={form.etd} onChange={e => setForm({ ...form, etd: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
              <Field label="Bow position (m from berth start)"><Input type="number" step="1" value={form.bow_position_m} onChange={e => setForm({ ...form, bow_position_m: parseFloat(e.target.value) })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
              <Field label="Purpose">
                <Select value={form.purpose} onValueChange={v => setForm({ ...form, purpose: v })}>
                  <SelectTrigger className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none">
                    {["passenger", "cargo", "bunker", "provisioning", "service"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Passenger count"><Input type="number" value={form.passenger_count} onChange={e => setForm({ ...form, passenger_count: parseInt(e.target.value) || 0 })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
              <div className="col-span-2 border border-[#1E293B] p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Required marine safety plans</div>
                <div className="grid sm:grid-cols-3 gap-2 text-xs text-slate-300">
                  {[
                    ["requires_manoeuvring_plan", "Vessel manoeuvring"],
                    ["requires_spatial_bathymetry", "Spatial bathymetry"],
                    ["requires_mooring_plan", "Mooring plan"],
                  ].map(([key, label]) => <label key={key} className="inline-flex items-center gap-2"><input type="checkbox" checked={form[key]} onChange={event => setForm({ ...form, [key]: event.target.checked })} />{label}</label>)}
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="btn-save-port-call" className="bg-sky-600 hover:bg-sky-500 rounded-none">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="panel p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
              <th className="text-left pb-2">Vessel</th><th className="text-left pb-2">Terminal</th><th className="text-left pb-2">Berth</th><th className="text-left pb-2">ETA</th><th className="text-left pb-2">ETD</th><th className="text-left pb-2">Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {list.map(p => (
              <tr key={p.id} className="border-b border-[#1E293B] hover:bg-[#0E1522]" data-testid={`port-call-row-${p.id}`}>
                <td className="py-2"><Ship size={12} className="inline mr-1 text-sky-400" />{vName(p.vessel_id)}</td>
                <td className="py-2 text-slate-400">{tName(p.terminal_id)}</td>
                <td className="font-mono py-2">{bName(p.berth_id)}</td>
                <td className="font-mono py-2">{p.eta?.slice(0, 16).replace("T", " ")}</td>
                <td className="font-mono py-2">{p.etd?.slice(0, 16).replace("T", " ")}</td>
                <td><span className={`font-mono text-[10px] uppercase px-2 py-0.5 ${p.status === "approved" ? "sev-ok" : p.status === "submitted" ? "sev-info" : "sev-warning"}`}>{p.status}</span></td>
                <td className="text-right"><Link to={`/port-calls/${p.id}`} data-testid={`open-port-call-${p.id}`} className="text-sky-400 hover:text-sky-300 text-xs font-mono uppercase tracking-widest">Open →</Link></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={7} className="text-slate-500 py-3 text-sm">No port calls.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
